import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import { agentNodeToAccount, type AgentNode } from '@/api/map/agent'
import { toUpdateAgentRequest } from '@/api/map/create'
import * as api from '@/data/api'
import type { Account } from '@/data/types'
import { keys } from '@/api/hooks/shared/keys'

async function updateAccountLive(input: api.UpdateAccountInput): Promise<Account> {
  const body = toUpdateAgentRequest({
    displayName: input.displayName,
    creditLimit: input.creditLimit,
    share: input.share,
    shareCasino: input.shareCasino,
    commission: input.commission,
    status: input.status,
    comments: input.comments,
    password: input.password,
    currencyType: input.currencyType,
    casinoCurrencyType: input.casinoCurrencyType,
  })
  const node = await apiFetch<AgentNode>(`/v1/agents/${input.accountId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
  const account = agentNodeToAccount(node)
  return {
    ...account,
    displayName: input.displayName.trim() || account.displayName,
    commission: input.commission,
    betLimits: input.betLimits,
    games: input.games,
    comments: input.comments,
    status: input.status,
  }
}

async function updateAccount(input: api.UpdateAccountInput): Promise<Account> {
  if (hasLiveApi()) return updateAccountLive(input)
  return api.updateAccount(input)
}

/** Persist edit of a direct downline child (live → PATCH /v1/agents/:id). */
export function useUpdateAccount() {
  const client = useQueryClient()
  const callerId = getCallerAgentId()
  return useMutation({
    mutationFn: updateAccount,
    onSuccess: (account) => {
      client.invalidateQueries({ queryKey: ['downline'] })
      client.invalidateQueries({ queryKey: ['children'] })
      client.invalidateQueries({ queryKey: keys.createParent })
      if (callerId) client.invalidateQueries({ queryKey: keys.account(callerId) })
      client.invalidateQueries({ queryKey: keys.account(account.id) })
      client.setQueryData(keys.account(account.id), account)
    },
  })
}
